import { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import type {
  SourceSnippetsData,
  StoredSource,
  SourceSnippet,
} from '@trinity/chat/types/extracted-answers.api';
import { BlockQuote } from '../presentation/BlockQuote';
import { Collapser, SeeMore } from '../presentation/SeeMore';

const Container = styled('div')({
  marginTop: 8,
});

const Toggle = styled('button')({
  background: 'none',
  border: 'none',
  color: 'var(--hyperlink)',
  cursor: 'pointer',
  fontSize: 14,
  padding: 0,
  '&:hover': {
    textDecoration: 'underline',
  },
});

const SourceList = styled('div')({
  marginTop: 4,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
});

const SourceLink = styled('a')({
  fontSize: 13,
  color: 'var(--hyperlink)',
  display: 'block',
  marginTop: 4,
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
});

const Highlight = styled('mark')({
  background: '#fff3cd',
  padding: '0 2px',
  borderRadius: 2,
});

const SourceContent = styled('div')({
  fontSize: 14,
  lineHeight: 1.6,
  whiteSpace: 'pre-wrap',
});

// ─── Highlight algorithm ─────────────────────────────────────────────────────

type Range = { start: number; end: number };

function findAllOccurrences(content: string, sentence: string): Range[] {
  const ranges: Range[] = [];
  const lowerContent = content.toLowerCase();
  const lowerSentence = sentence.toLowerCase();
  if (lowerSentence.length === 0) return ranges;

  let pos = 0;
  while (pos <= lowerContent.length - lowerSentence.length) {
    const idx = lowerContent.indexOf(lowerSentence, pos);
    if (idx === -1) break;
    ranges.push({ start: idx, end: idx + sentence.length });
    pos = idx + sentence.length;
  }
  return ranges;
}

function mergeRanges(ranges: Range[]): Range[] {
  if (ranges.length === 0) return [];
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged: Range[] = [sorted[0]!];
  for (let i = 1; i < sorted.length; i++) {
    const prev = merged[merged.length - 1];
    const curr = sorted[i];
    if (!prev || !curr) continue;
    if (curr.start <= prev.end) {
      prev.end = Math.max(prev.end, curr.end);
    } else {
      merged.push(curr);
    }
  }
  return merged;
}

function highlightSnippets(content: string, sentences: string[]): React.ReactNode[] {
  const allRanges: Range[] = [];
  for (const sentence of sentences) {
    allRanges.push(...findAllOccurrences(content, sentence));
  }

  const merged = mergeRanges(allRanges);
  if (merged.length === 0) return [content];

  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  for (let i = 0; i < merged.length; i++) {
    const range = merged[i];
    if (!range) continue;
    if (cursor < range.start) {
      nodes.push(content.slice(cursor, range.start));
    }
    nodes.push(<Highlight key={`hl-${i}`}>{content.slice(range.start, range.end)}</Highlight>);
    cursor = range.end;
  }
  if (cursor < content.length) {
    nodes.push(content.slice(cursor));
  }
  return nodes;
}

// ─── Component ───────────────────────────────────────────────────────────────

type Props = {
  sourceSnippetsData: SourceSnippetsData;
  transcriptId: number;
};

export function SourceSnippetPanel({ sourceSnippetsData, transcriptId }: Props): React.ReactElement | null {
  const [expanded, setExpanded] = useState(false);

  const { snippets, sources } = sourceSnippetsData;

  const groupedSources = useMemo(() => {
    const sourceMap = new Map<string, StoredSource>();
    for (const source of sources) {
      sourceMap.set(source.sourceId, source);
    }

    // Group snippets by sourceId, preserving first-appearance order
    const snippetsBySource = new Map<string, SourceSnippet[]>();
    for (const snippet of snippets) {
      const existing = snippetsBySource.get(snippet.sourceId);
      if (existing) {
        existing.push(snippet);
      } else {
        snippetsBySource.set(snippet.sourceId, [snippet]);
      }
    }

    const result: { source: StoredSource; sentences: string[] }[] = [];
    for (const [sourceId, sourceSnippets] of snippetsBySource) {
      const source = sourceMap.get(sourceId);
      if (source) {
        result.push({
          source,
          sentences: sourceSnippets.map((s) => s.sentence),
        });
      }
    }
    return result;
  }, [snippets, sources]);

  if (groupedSources.length === 0) return null;

  return (
    <Container>
      <Toggle onClick={() => setExpanded((v) => !v)}>
        {expanded ? 'Hide' : 'Show'} {groupedSources.length} source{groupedSources.length > 1 ? 's' : ''}
      </Toggle>
      {expanded && (
        <SourceList>
          {groupedSources.map(({ source, sentences }) => (
            <BlockQuote key={source.sourceId}>
              <SeeMore lineHeight={22} maxLines={8} CollapserComp={Collapser}>
                <SourceContent>{highlightSnippets(source.content, sentences)}</SourceContent>
              </SeeMore>
              <SourceLink
                href={`/workspaces/transcripts/${transcriptId}?s=${source.start}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                View in transcript
              </SourceLink>
            </BlockQuote>
          ))}
        </SourceList>
      )}
    </Container>
  );
}
